import { requireService, wrap } from '@repo/di-ez';
import { Table } from '@repo/go-table';
import type { GridApi, GridCellParams, GridColDef, GridColumnResizeParams } from '@repo/go-table';
import { hasValue } from '@repo/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'throttle-debounce';

import { getTrajStationTypeTKey, TRAJ_STATION_TYPES } from '../../consts/traj-station-type';
import type { FactTrajectoryPointEntity } from '../../service/entities';
import { transformAppLanguageToTableLanguageToken } from '../../utils/table-language';

import { SERVICE_COLUMNS } from './display-manager';
import { useTableEventHandlers } from './hooks/use-table-event-handlers';
import { TrajectoryTableStore } from './trajectory-table.store';
import { isColumnsOrderModelEqual, isValidColumnsOrderModel } from './utils/columns-order-model';

type Props = {
  language: string;
  columns: GridColDef[];
  tableApi: MutableRefObject<GridApi>;
  isEditing?: boolean;
};

export const TrajectoryTable = wrap<Props>(function TrajectoryTable({
  language,
  columns,
  tableApi,
  isEditing = false,
}) {
  const trajectoryService = requireService('trajectoryService');
  const validationStore = requireService('trajectoryTableValidationStore');
  const uiStore = requireService('trajectoryUiStore');
  const displayManager = uiStore.displayManager;
  const { t } = useTranslation();

  const tableLanguage = useMemo(() => transformAppLanguageToTableLanguageToken(language), [language]);

  const typeTrajStationOptions = useMemo(
    () =>
      TRAJ_STATION_TYPES.map((value) => ({
        value,
        label: t(getTrajStationTypeTKey(value)),
      })),
    [t],
  );

  const columnFields = useMemo(() => columns.map((column) => column.field), [columns]);
  const { mainFactTrajectory } = trajectoryService;
  const mdTieOnMeasuredDepth = mainFactTrajectory?.mdTieOnMeasuredDepth ?? null;
  const trajectorySource = mainFactTrajectory?.data.source ?? null;
  const { onCopy, onBeforeClipboardPasteStart } = useTableEventHandlers({
    tableApi,
    isEditing,
    typeTrajStationOptions,
  });
  const [store] = useState(() => new TrajectoryTableStore(tableApi, trajectoryService, validationStore));

  useEffect(() => store.init(), [store]);

  const isCellEditable = useCallback(
    (params: GridCellParams<FactTrajectoryPointEntity>) => {
      if (!isEditing || uiStore.isUiLocked || !params.colDef.editable) {
        return false;
      }

      return !trajectoryService.isMainPointLocked(params.row);
    },
    [isEditing, trajectoryService, uiStore.isUiLocked],
  );

  const isRowSelectable = useCallback(
    (params: { row: FactTrajectoryPointEntity }) => {
      if (!isEditing) {
        return true;
      }

      return !trajectoryService.isMainPointLocked(params.row);
    },
    [isEditing, trajectoryService],
  );

  const getCellClassName = useCallback(
    (params: GridCellParams<FactTrajectoryPointEntity>): string => {
      if (!isEditing) {
        return '';
      }

      if (SERVICE_COLUMNS.includes(params.field) || params.field === '__check__') {
        return '';
      }

      if (trajectoryService.isMainPointLocked(params.row) || !params.colDef.editable) {
        return 'go-table__disabled';
      }

      return '';
    },
    [isEditing, trajectoryService],
  );

  useEffect(() => {
    const points = mainFactTrajectory?.points;

    if (!points?.length) {
      return;
    }

    tableApi.current.updateRows(points);
    // TODO: need to resolve hidden dependencies
  }, [mainFactTrajectory, mdTieOnMeasuredDepth, tableApi, trajectorySource]);

  const columnsOrderModel = displayManager.columnsOrderModel;
  const columnsWidth = displayManager.columnsWidth;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleWidthChange = useCallback(
    debounce(300, ({ colDef: { field }, width }: GridColumnResizeParams) => {
      const tableColumns = tableApi.current.getAllColumns();
      const updatedColumns: Record<string, number> = {};

      tableColumns.forEach((column) => {
        if (column.field === field) {
          updatedColumns[field] = width;
        } else if (columnsWidth && !columnsWidth[column.field]) {
          updatedColumns[column.field] = column.computedWidth;
        }
      });

      displayManager.onWidthChange(updatedColumns);
    }),
    [columnsWidth, displayManager],
  );

  const handleColumnsOrderModelChange = useCallback(
    (orderModel: string[]) => {
      const isOrderModelValid =
        orderModel.length === columnFields.length && orderModel.every((field) => columnFields.includes(field));

      if (!isOrderModelValid) {
        return;
      }

      const hasSavedOrderModel = Boolean(columnsOrderModel?.length);
      const hasCustomSavedOrderModel = hasSavedOrderModel && !isColumnsOrderModelEqual(columnsOrderModel, columnFields);
      const isIncomingDefaultOrderModel = isColumnsOrderModelEqual(orderModel, columnFields);

      // Mount/rebuild noise from go-table: keep custom order, ignore default overwrite.
      if (hasCustomSavedOrderModel && isIncomingDefaultOrderModel) {
        return;
      }

      displayManager.onColumnsOrderModelChange(orderModel);
    },
    [columnFields, columnsOrderModel, displayManager],
  );

  useEffect(() => {
    return tableApi.current.subscribeEvent('columnSeparatorDoubleClick', ({ field }) => {
      const col = tableApi.current.getColumn(field);
      const width = col.width;

      if (hasValue(width)) {
        handleWidthChange({ colDef: col, width });
      }
    });
  }, [handleWidthChange, tableApi]);

  useEffect(() => {
    if (!columnFields.length) {
      return;
    }

    const saved = displayManager.columnsOrderModel;

    if (!saved?.length || !isValidColumnsOrderModel(saved, columns)) {
      displayManager.onColumnsOrderModelChange(columnFields);
    }
  }, [columnFields, columns, displayManager]);

  useEffect(() => {
    if (!columnsWidth) {
      return;
    }

    const updates: GridColDef[] = [];

    for (const column of tableApi.current.getAllColumns()) {
      const width = columnsWidth[column.field];

      if (!hasValue(width) || width === column.width) {
        continue;
      }

      updates.push({ ...column, width });
    }

    if (updates.length > 0) {
      tableApi.current.updateColumns(updates);
    }
  }, [tableApi, columnsWidth]);

  return (
    <Table<FactTrajectoryPointEntity>
      initialState={{
        sorting: {
          sortModel: displayManager.sortingModel,
        },
        columns: {
          columnVisibilityModel: displayManager.columnsVisibilityModel,
        },
        pinnedColumns: displayManager.pinnedColumns,
      }}
      sortModel={displayManager.sortingModel}
      columnVisibilityModel={displayManager.columnsVisibilityModel}
      columnsOrderModel={displayManager.columnsOrderModel}
      onColumnVisibilityModelChange={displayManager.onColumnVisibilityChange}
      onColumnsOrderModelChange={handleColumnsOrderModelChange}
      onPinnedColumnsChange={displayManager.onPinChange}
      onSortModelChange={displayManager.onSortChange}
      onColumnResize={handleWidthChange}
      checkboxSelection={uiStore.isTableCheckboxesVisible || isEditing}
      rowSelectionModel={uiStore.selectedRowIds}
      onRowSelectionModelChange={uiStore.setSelectedRowIds}
      isRowSelectable={isRowSelectable}
      cellSelection
      onBeforeClipboardPasteStart={onBeforeClipboardPasteStart}
      onClipboardCopy={onCopy}
      serviceColumns={SERVICE_COLUMNS}
      getRowId={(row) => row.entityId}
      apiRef={tableApi}
      columns={columns}
      rows={mainFactTrajectory?.points ?? []}
      language={tableLanguage}
      loading={uiStore.isUiLocked}
      editMode="cell"
      isCellEditable={isCellEditable}
      getCellClassName={getCellClassName}
      columnHeaderOrientation={displayManager.columnHeaderOrientation}
      columnHeaderHeight={displayManager.columnHeaderHeight}
      columnHeaderHeightResizable={displayManager.isColumnHeaderHeightResizable}
      onColumnHeaderHeightChange={displayManager.onColumnHeaderHeightChange}
    />
  );
});
